/* eslint-disable react/no-array-index-key */
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import {
    Box, Tabs, Tab, Typography, TextField, Button,
    Stack, IconButton, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, alpha, Chip,
} from '@mui/material';
import { m, AnimatePresence } from 'framer-motion';
import Iconify from 'src/components/Iconify';
import TreatmentPlanCostTable from './TreatmentPlanCostTable';

const MAX_PACKAGES = 3;

function createBlankPackage(name = '') {
    return {
        id: uuidv4(),
        name,
        costItems: [],
        deposit: '',
        paymentPlan: { type: 'no', details: '' },
        included: '',
        excluded: '',
        phases: [],
    };
}

// ─── PackagePanel ─────────────────────────────────────────────────────────────

function PackagePanel({ package: pkg, onChange, readOnly, treatments: practiceTreatments }) {
    const handleField = (field, value) => onChange({ ...pkg, [field]: value });
    const handleCostChange = (field, value) => onChange({ ...pkg, [field]: value });

    // Extract treatment names from costItems for summary chips
    const treatments = (pkg.costItems || []).reduce((acc, item) => {
        if (item.treatmentName && !acc.some((t) => t.name === item.treatmentName && t.colour === item.colour)) {
            acc.push({ name: item.treatmentName, colour: item.colour || '#00AB55' });
        }
        return acc;
    }, []);

    return (
        <AnimatePresence mode="wait">
            <m.div
                key={pkg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
            >
                <Box sx={{ pt: 2 }}>
                    {/* Package name (always visible) */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Package name
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="e.g. Standard, Premium"
                            value={pkg.name || ''}
                            onChange={(e) => handleField('name', e.target.value)}
                            disabled={readOnly}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px', fontSize: '13px', fontWeight: 600, bgcolor: '#fff',
                                    '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
                                    '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
                                },
                                '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
                            }}
                        />
                    </Box>

                    {/* Treatment summary chips */}
                    {treatments.length > 0 && (
                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Treatments included
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {treatments.map((treatment, idx) => (
                                    <Chip
                                        key={idx}
                                        label={treatment.name}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            borderRadius: '6px',
                                            borderColor: treatment.colour,
                                            color: treatment.colour,
                                            bgcolor: alpha(treatment.colour, 0.08),
                                            height: 28,
                                            '& .MuiChip-label': { px: 1 },
                                        }}
                                        avatar={
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: treatment.colour,
                                                }}
                                            />
                                        }
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Costs & Payment */}
                    <TreatmentPlanCostTable
                        costItems={pkg.costItems || []}
                        deposit={pkg.deposit}
                        paymentPlan={pkg.paymentPlan}
                        included={pkg.included}
                        excluded={pkg.excluded}
                        onChange={handleCostChange}
                        readOnly={readOnly}
                        treatments={practiceTreatments}
                    />
                </Box>
            </m.div>
        </AnimatePresence>
    );
}

PackagePanel.propTypes = {
    package:    PropTypes.object.isRequired,
    onChange:   PropTypes.func.isRequired,
    readOnly:   PropTypes.bool,
    treatments: PropTypes.array,
};
PackagePanel.defaultProps = { readOnly: false, treatments: [] };

// ─── Main export ──────────────────────────────────────────────────────────────

export default function TreatmentPlanOptionTabs({
    options,
    onChange,
    // eslint-disable-next-line no-unused-vars
    autoFilledFields,
    readOnly,
    treatments,
}) {
    const [activeTab, setActiveTab] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const updatePackage = (idx, updated) => {
        const next = [...options];
        next[idx] = updated;
        onChange(next);
    };

    const addPackage = () => {
        if (options.length >= MAX_PACKAGES) return;
        const next = [...options, createBlankPackage(`Package ${options.length + 1}`)];
        onChange(next);
        setActiveTab(next.length - 1);
    };

    const duplicatePackage = (idx) => {
        if (options.length >= MAX_PACKAGES) return;
        const copy = { ...options[idx], id: uuidv4(), name: `${options[idx].name || `Package ${idx + 1}`} (copy)` };
        const next = [...options.slice(0, idx + 1), copy, ...options.slice(idx + 1)];
        onChange(next);
        setActiveTab(idx + 1);
    };

    const requestDelete = (idx) => {
        const hasData = options[idx]?.name || (options[idx]?.costItems?.length > 0);
        if (hasData) setDeleteConfirm(idx);
        else doDelete(idx);
    };

    const doDelete = (idx) => {
        const next = options.filter((_, i) => i !== idx);
        onChange(next);
        setDeleteConfirm(null);
        if (activeTab >= next.length) setActiveTab(Math.max(0, next.length - 1));
    };

    return (
        <Box>
            {/* Tab bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(145,158,171,0.16)', mb: 0 }}>
                <Tabs
                    value={Math.min(activeTab, options.length - 1)}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{
                        flex: 1, minHeight: 42,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '13px', minHeight: 42, px: 1.5, py: 0, color: 'text.secondary' },
                        '& .Mui-selected': { color: '#00AB55' },
                        '& .MuiTabs-indicator': { bgcolor: '#00AB55', height: 2 },
                    }}
                >
                    {options.map((opt, idx) => (
                        <Tab
                            key={opt.id}
                            label={
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <span>{opt.name || `Package ${idx + 1}`}</span>
                                    {!readOnly && options.length > 1 && (
                                        <Tooltip title="Remove package">
                                            <IconButton
                                                size="small"
                                                component="span"
                                                onClick={(e) => { e.stopPropagation(); requestDelete(idx); }}
                                                sx={{ p: 0, width: 16, height: 16, color: '#C4CDD5', '&:hover': { color: '#FF4842' } }}
                                            >
                                                <Iconify icon="eva:close-fill" width={12} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            }
                        />
                    ))}
                </Tabs>

                {!readOnly && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1, flexShrink: 0 }}>
                        {options.length > 0 && (
                            <Tooltip title="Duplicate this package">
                                <IconButton
                                    size="small"
                                    onClick={() => duplicatePackage(activeTab)}
                                    disabled={options.length >= MAX_PACKAGES}
                                    sx={{ color: '#C4CDD5', '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' }, '&.Mui-disabled': { opacity: 0.3 } }}
                                >
                                    <Iconify icon="eva:copy-fill" width={15} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {options.length < MAX_PACKAGES && (
                            <Tooltip title="Offer up to 3 packages for the patient to compare">
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Iconify icon="eva:plus-fill" width={13} />}
                                        onClick={addPackage}
                                        disabled={options.length >= MAX_PACKAGES}
                                        sx={{
                                            fontSize: '12px', fontWeight: 600, textTransform: 'none',
                                            borderRadius: '7px', py: '3px', px: '10px',
                                            borderColor: 'rgba(145,158,171,0.32)', color: '#637381',
                                            '&:hover': { borderColor: '#00AB55', color: '#00AB55', bgcolor: alpha('#00AB55', 0.04) },
                                        }}
                                    >
                                        Add Package
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Box>

            {/* MAX hint */}
            {options.length >= MAX_PACKAGES && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '11px', mt: 0.75, mb: 0, display: 'block' }}>
                    Maximum 3 packages — patients compare these side by side to choose their preferred option
                </Typography>
            )}

            {/* Active package panel */}
            {options.length > 0 && (
                <PackagePanel
                    package={options[Math.min(activeTab, options.length - 1)]}
                    onChange={(updated) => updatePackage(Math.min(activeTab, options.length - 1), updated)}
                    readOnly={readOnly}
                    treatments={treatments}
                />
            )}

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteConfirm !== null}
                onClose={() => setDeleteConfirm(null)}
                PaperProps={{ sx: { borderRadius: '14px', maxWidth: 380 } }}
            >
                <DialogTitle sx={{ fontSize: '16px', fontWeight: 700, pb: 1 }}>
                    Remove package?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                        This will remove &quot;{options[deleteConfirm]?.name || `Package ${(deleteConfirm ?? 0) + 1}`}&quot; and all its data. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        size="small" variant="outlined"
                        onClick={() => setDeleteConfirm(null)}
                        sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'rgba(145,158,171,0.3)', color: '#637381' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small" variant="contained"
                        onClick={() => doDelete(deleteConfirm)}
                        sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#FF4842', '&:hover': { bgcolor: '#B71C1C' } }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

TreatmentPlanOptionTabs.propTypes = {
    options:          PropTypes.array.isRequired,
    onChange:         PropTypes.func.isRequired,
    autoFilledFields: PropTypes.array,
    readOnly:         PropTypes.bool,
    treatments:       PropTypes.array,
};

TreatmentPlanOptionTabs.defaultProps = {
    autoFilledFields: [],
    readOnly:         false,
    treatments:       [],
};
